import { Body4, Body5, H5 } from "client/src/components/Typography/Typography";
import { useGetPublicExplorerPageById } from "client/src/hooks/ExplorerPage/useExplorerPages";
import { useGetActiveRouteData } from "client/src/hooks/route";
import { useMediaQuery } from "client/src/hooks/useMediaQuery";
import clsx from "clsx";
import { VIEW_TYPE } from "shared/config/routeData";

import * as styles from "./legalFooter.module.less";
import type { PublicExplorerPage } from "shared/types/ExplorerPage";

export const DataTestId = {
  Copyright: "copyright",
  LegalLink: "legal-link",
  PrivacyLink: "privacy-link",
  SecurityLink: "security-link",
  FraudLink: "fraud-link",
};

export function LegalFooter() {
  const activeRouteData = useGetActiveRouteData();
  const { data: explorerPage } = useGetPublicExplorerPageById(
    activeRouteData?.params.explorerPageId || "",
  );
  return <LegalFooterUI activeRouteData={activeRouteData} explorerPage={explorerPage} />;
}

const LegalCopyYear = `© ${new Date().getFullYear()}`;

type LegalFooterUIProps = {
  activeRouteData?: ReturnType<typeof useGetActiveRouteData>;
  explorerPage?: PublicExplorerPage;
};
export const LegalFooterUI = (props: LegalFooterUIProps) => {
  const { activeRouteData, explorerPage } = props;

  const isBenEx = activeRouteData?.viewType === VIEW_TYPE.explorer;
  const isCustomBranded =
    !!explorerPage?.customBranding && !!explorerPage.benefitsExplorerCustomBranding;
  const isNonSunLifeClient = !!explorerPage?.nonSunLifeBenefitsExplorer;

  const hideSlogan = isBenEx && (isCustomBranded || isNonSunLifeClient);
  const showFullscopeLinks = isBenEx && isNonSunLifeClient;
  const showFraud = isBenEx && !(isCustomBranded && isNonSunLifeClient);

  const isPrint = useMediaQuery("print");
  return (
    <div className={styles.legalFooterContainer}>
      <div className={clsx(styles.content, "p-[32px]")}>
        {!hideSlogan && (
          <H5 as="p" yellow={!isPrint}>
            Life’s brighter under the sun
          </H5>
        )}

        <div className={styles.flex}>
          <div className={styles.text}>
            {!isBenEx && (
              <Body5 as="p" white={!isPrint}>
                In all states, except New York, insurance products are underwritten by Sun Life
                Assurance Company of Canada (Wellesley Hills, MA) ("SLOC"). In certain states,
                prepaid dental products are provided by SLOC or by prepaid dental companies
                affiliated with SLOC. In New York, insurance products and prepaid dental products
                are underwritten or provided by Sun Life and Health Insurance Company (U.S.)
                (Lansing, MI).
              </Body5>
            )}

            {explorerPage?.hideSunLifeLogo ? (
              <Body5 as="p" white={!isPrint}>
                {isNonSunLifeClient
                  ? `${LegalCopyYear} FullscopeRMS and/or its affiliates. FullscopeRMS is a brand name used by Disability Reinsurance Management Services, Inc. (DRMS). DRMS is the third-party administrator for certain products offered.`
                  : `${LegalCopyYear} Sun Life Assurance Company of Canada, Wellesley Hills,
                MA 02481. All rights reserved. Visit us at `}
                <a
                  href={
                    showFullscopeLinks
                      ? "https://www.fullscoperms.com/en/"
                      : "https://www.sunlife.com/us"
                  }
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={DataTestId.Copyright}
                >
                  <Body5 white={!isPrint}>
                    {showFullscopeLinks ? "www.fullscoperms.com/en/" : "www.sunlife.com/us"}
                  </Body5>
                </a>
                .
              </Body5>
            ) : (
              <Body5 as="p" white={!isPrint}>
                {isNonSunLifeClient
                  ? `${LegalCopyYear} FullscopeRMS and/or its affiliates. FullscopeRMS is a brand name used by Disability Reinsurance Management Services, Inc. (DRMS). DRMS is the third-party administrator for certain products offered.`
                  : `${LegalCopyYear} Sun Life Assurance Company of Canada, Wellesley Hills,
                MA 02481. All rights reserved. The Sun Life name and logo are registered trademarks
                of Sun Life Assurance Company of Canada. Visit us at `}
                <a
                  href={
                    showFullscopeLinks
                      ? "https://www.fullscoperms.com/en/"
                      : "https://www.sunlife.com/us"
                  }
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={DataTestId.Copyright}
                >
                  <Body5 white={!isPrint}>
                    {showFullscopeLinks ? "www.fullscoperms.com/en/" : "www.sunlife.com/us"}
                  </Body5>
                </a>
                .
              </Body5>
            )}
          </div>

          {!isPrint && (
            <div className={styles.links}>
              <a
                href={
                  showFullscopeLinks
                    ? "https://www.fullscoperms.com/en/legal/"
                    : "https://www.sunlife.com/sl/pslf-united-states/en/legal/?vgnLocale=en_US"
                }
                target="_blank"
                rel="noreferrer noopener"
                data-testid={DataTestId.LegalLink}
              >
                <Body4 white={!isPrint}>Legal</Body4>
                <span className="sr-only">(opens in a new tab)</span>
              </a>

              <a
                href={
                  showFullscopeLinks
                    ? "https://www.fullscoperms.com/en/privacy/"
                    : "https://www.sunlife.com/sl/pslf-united-states/en/privacy/?vgnLocale=en_US"
                }
                target="_blank"
                rel="noreferrer noopener"
                data-testid={DataTestId.PrivacyLink}
              >
                <Body4 white={!isPrint}>Privacy</Body4>
                <span className="sr-only">(opens in a new tab)</span>
              </a>

              <a
                href={
                  showFullscopeLinks
                    ? "https://www.fullscoperms.com/en/security/"
                    : "https://www.sunlife.com/sl/pslf-united-states/en/security/?vgnLocale=en_US"
                }
                target="_blank"
                rel="noreferrer noopener"
                data-testid={DataTestId.SecurityLink}
              >
                <Body4 white={!isPrint}>Security</Body4>
                <span className="sr-only">(opens in a new tab)</span>
              </a>

              {showFraud && (
                <a
                  href="https://www.sunlife.com/sl/pslf-united-states/en/fraud/?vgnLocale=en_US"
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={DataTestId.FraudLink}
                >
                  <Body4 white={!isPrint}>Fraud</Body4>
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              )}
            </div>
          )}
        </div>
        {isBenEx && (
          <div className={styles.flex}>
            <div className={styles.text}>
              <Body5 as="p" white={!isPrint}>
                GGWC-EE-10777
              </Body5>
            </div>
            <Body5 as="p" white={!isPrint}>
              #1478905838
            </Body5>
          </div>
        )}
      </div>
    </div>
  );
};
